import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, AlertTriangle } from "lucide-react";
import { api, fmtCHF, fmtDate } from "@/lib/api";
import { SectionTitle } from "@/components/Shared";
import DocDetail from "@/components/DocDetail";
import { useCompany } from "@/context/CompanyContext";
import { PAYMENT_STATUS_LABELS } from "@/lib/organs";

const PAYMENT_LABELS = { contanti: "Contanti", carta_aziendale: "Carta aziendale", carta_personale: "Carta personale", banca: "Banca", altro: "Altro", da_determinare: "Da determinare" };

export default function AccountingPage() {
  const { company } = useCompany();
  const [period, setPeriod] = useState(new Date().toISOString().slice(0, 7));
  const [summary, setSummary] = useState(null);
  const [kind, setKind] = useState(null);
  const [records, setRecords] = useState([]);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    setSummary(null);
    setKind(null);
    api.get(`/accounting/summary?period=${period}`).then((r) => setSummary(r.data)).catch(() => {});
  }, [period]);

  const drill = async (k) => {
    setKind(k);
    const { data } = await api.get(`/accounting/records?period=${period}&kind=${k}`);
    setRecords(data);
  };

  const openDoc = async (docId) => {
    const { data } = await api.get(`/documents/${docId}`);
    setDetail(data);
  };

  const kpis = summary ? [
    { key: "spese", label: "Spese totali", value: fmtCHF(summary.spese_totali.value), sub: `${summary.spese_totali.count} documenti`, color: "#00E5FF" },
    { key: "fatture_da_pagare", label: "Fatture da pagare", value: fmtCHF(summary.fatture_da_pagare.value), sub: `${summary.fatture_da_pagare.count} aperte`, color: "#F59E0B" },
    { key: "fatture_pagate", label: "Fatture pagate", value: fmtCHF(summary.fatture_pagate.value), sub: `${summary.fatture_pagate.count} fatture`, color: "#10B981" },
    { key: "spese_cash", label: "Spese cash", value: fmtCHF(summary.spese_cash.value), sub: `${summary.spese_cash.count} movimenti cassa`, color: "#9DF3FF" },
    { key: "spese_carta", label: "Spese con carta", value: fmtCHF(summary.spese_carta.value), sub: `${summary.spese_carta.da_riconciliare} da riconciliare`, color: "#3B82F6" },
    { key: "note_spese", label: "Note spese", value: fmtCHF(summary.note_spese.value), sub: `${summary.note_spese.count} note`, color: "#9DF3FF" },
  ] : [];

  return (
    <div className="max-w-4xl mx-auto px-4" data-testid="accounting-page">
      <Link to="/" className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-cyan-300 mb-4"><ChevronLeft size={14} /> Organismo</Link>
      <div className="flex items-start justify-between gap-3 mb-5 flex-wrap fade-up">
        <div>
          <p className="text-xs tracking-[0.25em] text-gray-500">CIRCOLAZIONE · METAFORA ORGANISMO</p>
          <h1 className="font-display text-3xl font-bold mt-1">Contabilità & IVA</h1>
          <p className="text-sm text-gray-500 mt-1">Registro Contabile DAD · pre-contabilità operativa. Ogni numero si apre fino al documento originale.</p>
        </div>
        <input type="month" data-testid="accounting-period" value={period} onChange={(e) => setPeriod(e.target.value)}
          className="input-dark !w-auto !py-2 text-sm" />
      </div>

      {summary && summary.records_total === 0 && summary.elaborati.count === 0 && (
        <div className="glass rounded-3xl p-8 text-center mb-5" data-testid="accounting-empty">
          <p className="text-gray-400 text-sm font-medium">DATI INSUFFICIENTI</p>
          <p className="text-xs text-gray-600 mt-2">Nessuna registrazione nel periodo. Acquisisci documenti con DAD Scan: DAD li registrerà automaticamente.</p>
        </div>
      )}

      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-5" data-testid="accounting-kpis">
          {kpis.map((k) => (
            <button key={k.key} data-testid={`kpi-${k.key}`} onClick={() => drill(k.key)}
              className={`glass rounded-3xl p-4 text-left transition-colors hover:border-cyan-400/30 ${kind === k.key ? "!border-cyan-400/50" : ""}`}>
              <div className="text-[10px] tracking-[0.15em] text-gray-500 uppercase">{k.label}</div>
              <div className="font-display font-bold text-xl mt-1" style={{ color: k.color }}>{k.value}</div>
              <div className="text-[11px] text-gray-500 mt-0.5">{k.sub}</div>
            </button>
          ))}
        </div>
      )}

      {summary && (
        <div className="grid grid-cols-2 gap-3 mb-5">
          <Link to="/actions" data-testid="kpi-eccezioni" className="glass rounded-3xl p-4 hover:border-red-400/30 transition-colors block">
            <div className="text-[10px] tracking-[0.15em] text-gray-500 uppercase flex items-center gap-1.5"><AlertTriangle size={11} /> Documenti in eccezione</div>
            <div className="font-display font-bold text-xl mt-1" style={{ color: summary.eccezioni.count > 0 ? "#EF4444" : "#10B981" }}>{summary.eccezioni.count}</div>
            <div className="text-[11px] text-gray-500 mt-0.5">{summary.eccezioni.count > 0 ? "richiedono la tua decisione" : "nessuna"}</div>
          </Link>
          <div className="glass rounded-3xl p-4" data-testid="kpi-elaborati">
            <div className="text-[10px] tracking-[0.15em] text-gray-500 uppercase">Documenti elaborati</div>
            <div className="font-display font-bold text-xl mt-1 text-emerald-400">{summary.elaborati.count}</div>
            <div className="text-[11px] text-gray-500 mt-0.5">archiviati nel periodo</div>
          </div>
        </div>
      )}

      {summary?.fatture_da_pagare.next?.length > 0 && (
        <div className="glass rounded-3xl p-5 mb-5" data-testid="payables-next">
          <SectionTitle>Prossime scadenze fornitori</SectionTitle>
          {summary.fatture_da_pagare.next.map((p, i) => (
            <div key={i} className="flex items-center justify-between rounded-2xl px-3 py-2.5 text-sm">
              <span className="text-gray-200">{p.supplier || "—"}</span>
              <span className="text-gray-500 text-xs">{p.due_date ? fmtDate(p.due_date) : "senza scadenza"}</span>
              <span className="font-mono">{fmtCHF(p.amount)}</span>
            </div>
          ))}
        </div>
      )}

      {kind && (
        <div className="glass rounded-3xl p-5 fade-up" data-testid="accounting-drilldown">
          <SectionTitle>{kpis.find((k) => k.key === kind)?.label} · dettaglio</SectionTitle>
          <div className="space-y-1">
            {records.map((r) => (
              <button key={r.record_id} data-testid={`record-${r.record_id}`} onClick={() => openDoc(r.document_id)}
                className="w-full text-left flex items-center justify-between gap-3 rounded-2xl px-3 py-2.5 hover:bg-white/[0.03] transition-colors">
                <div className="min-w-0">
                  <div className="text-sm text-gray-200 truncate">{r.supplier || "—"}</div>
                  <div className="text-xs text-gray-500 truncate">
                    {r.document_type} · {fmtDate(r.document_date)} · {PAYMENT_LABELS[r.actual_payment_method] || r.actual_payment_method || "—"}
                    {r.management_category ? ` · ${r.management_category}` : ""}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="chip !text-[10px]" style={{ color: (PAYMENT_STATUS_LABELS[r.payment_status] || {}).color }}>
                    {(PAYMENT_STATUS_LABELS[r.payment_status] || {}).label || r.payment_status}
                  </span>
                  <span className="font-mono text-sm">{fmtCHF(r.gross_amount)}</span>
                </div>
              </button>
            ))}
            {records.length === 0 && <p className="text-sm text-gray-500 px-3 py-4">Nessun record in questa vista per il periodo.</p>}
          </div>
        </div>
      )}

      {company?.demo && <p className="text-[11px] text-amber-300/70 mt-4">I dati statici di questa azienda demo sono dimostrativi; le registrazioni DAD da Scan sono reali.</p>}
      <DocDetail detail={detail} onClose={() => setDetail(null)} />
    </div>
  );
}
